import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Search } from "lucide-react";

import { GlobalAppMenu } from "@/components/nav/GlobalAppMenu";
import { useNewsArticleQuery, useNewsFeedQuery } from "@/features/workbench/queries";
import { apiClient } from "@/services/api/client";

function readArticleParam() {
  if (typeof window === "undefined") {
    return "";
  }
  const params = new URLSearchParams(window.location.search);
  return String(params.get("article") || "").trim();
}

function writeArticleParam(articlePath: string) {
  if (typeof window === "undefined") {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  if (articlePath) {
    params.set("article", articlePath);
  } else {
    params.delete("article");
  }
  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}`;
  window.history.replaceState(null, "", nextUrl);
}

export function NewsWorkbenchPage() {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<string>("");
  const [keywordInput, setKeywordInput] = useState<string>("");
  const keyword = useDeferredValue(keywordInput.trim());
  const [selectedArticlePath, setSelectedArticlePath] = useState<string>(() => readArticleParam());
  const [metaCategory, setMetaCategory] = useState<string>("");
  const [metaTags, setMetaTags] = useState<string>("");

  const bootstrapQuery = useQuery({
    queryKey: ["terminal", "bootstrap"],
    queryFn: () => apiClient.bootstrap(),
    staleTime: 30_000,
  });
  const isAdmin = Boolean(bootstrapQuery.data?.current_user?.is_admin);

  const feedQuery = useNewsFeedQuery({
    category: category || undefined,
    keyword: keyword || undefined,
    limit: 80,
    offset: 0,
  });
  const items = feedQuery.data?.items || [];
  const categories = feedQuery.data?.categories || [];

  useEffect(() => {
    if (selectedArticlePath) {
      return;
    }
    if (!items.length) {
      return;
    }
    setSelectedArticlePath(items[0].path);
  }, [items, selectedArticlePath]);

  useEffect(() => {
    writeArticleParam(selectedArticlePath);
  }, [selectedArticlePath]);

  const articleQuery = useNewsArticleQuery(selectedArticlePath || null);
  const activePath = articleQuery.data?.path || selectedArticlePath;

  useEffect(() => {
    const article = articleQuery.data;
    if (!article) {
      return;
    }
    setMetaCategory(String(article.category || ""));
    setMetaTags(String(article.tags_display || ""));
  }, [articleQuery.data?.category, articleQuery.data?.path, articleQuery.data?.tags_display]);

  const updateMetaMutation = useMutation({
    mutationFn: async () =>
      apiClient.updateNewsMeta({
        article_path: selectedArticlePath,
        category: metaCategory,
        tags: metaTags,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["news", "feed"] }),
        queryClient.invalidateQueries({ queryKey: ["news", "article", selectedArticlePath || ""] }),
      ]);
      if (selectedArticlePath) {
        await articleQuery.refetch();
      }
    },
  });

  const articleHeader = useMemo(
    () => ({
      title: articleQuery.data?.title || "请选择左侧资讯",
      category: articleQuery.data?.category || "--",
      published: articleQuery.data?.published_display || "--",
      tags: articleQuery.data?.tags_display || "--",
    }),
    [articleQuery.data?.category, articleQuery.data?.published_display, articleQuery.data?.tags_display, articleQuery.data?.title],
  );
  const adminCategoryOptions = useMemo(() => {
    const merged = [...categories];
    if (metaCategory && !merged.includes(metaCategory)) {
      merged.unshift(metaCategory);
    }
    if (!merged.length) {
      merged.push("投资研报");
    }
    return merged;
  }, [categories, metaCategory]);

  const pageError =
    (feedQuery.error as Error | null)?.message ||
    (articleQuery.error as Error | null)?.message ||
    (updateMetaMutation.error as Error | null)?.message ||
    "";

  return (
    <main className="flex h-dvh min-h-0 flex-col overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] bg-[#101318]">
        <div className="flex h-12 items-center gap-3 px-3">
          <GlobalAppMenu className="shrink-0" />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-slate-100">市场大盘与情报</h1>
            <p className="text-[11px] text-[var(--muted)]">情报列表 / 分类筛选 / 正文阅读</p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative w-52">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-slate-500" />
              <input
                className="h-8 w-full rounded-md border border-[var(--border)] bg-[#0c0f14] pl-7 pr-2 text-xs text-slate-100"
                placeholder="搜索标题/摘要/标签"
                value={keywordInput}
                onChange={(event) => setKeywordInput(event.target.value)}
              />
            </div>
            <select
              className="h-8 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-slate-100"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="">全部分类</option>
              {categories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <button
              className="flex h-8 items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-[var(--muted)] hover:text-slate-100"
              onClick={() => {
                void bootstrapQuery.refetch();
                void feedQuery.refetch();
                if (selectedArticlePath) {
                  void articleQuery.refetch();
                }
              }}
            >
              <RefreshCw className="size-3.5" />
              刷新
            </button>
          </div>
        </div>
      </header>

      {pageError ? (
        <div className="border-b border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{pageError}</div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-[360px_minmax(0,1fr)]">
        <aside className="flex min-h-0 min-w-0 flex-col border-r border-[var(--border)] bg-[#0f1115]">
          <div className="border-b border-[var(--border)] px-3 py-2 text-xs text-[var(--muted)]">
            共 {feedQuery.data?.total ?? 0} 条
          </div>
          <div className="terminal-scrollbar min-h-0 flex-1 overflow-auto">
            {items.map((item) => {
              const active = item.path === activePath;
              return (
                <button
                  key={item.path}
                  className={`w-full border-b border-[var(--border)]/60 px-3 py-2 text-left ${active ? "bg-sky-500/10" : "hover:bg-white/5"}`}
                  onClick={() => setSelectedArticlePath(item.path)}
                >
                  <div className="line-clamp-2 text-sm font-semibold text-slate-100">{item.title}</div>
                  <div className="mt-1 line-clamp-2 text-[11px] leading-5 text-slate-400">{item.summary || "--"}</div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-[var(--muted)]">
                    <span className="truncate">{item.category || "--"}</span>
                    <span className="shrink-0">{item.published_display || "--"}</span>
                  </div>
                </button>
              );
            })}
            {!items.length ? <div className="px-3 py-8 text-center text-[11px] text-[var(--muted)]">暂无资讯</div> : null}
          </div>
        </aside>

        <section className="flex min-h-0 min-w-0 flex-col bg-[#0d1015]">
          <div className="border-b border-[var(--border)] px-3 py-2">
            <div className="text-sm font-semibold text-slate-100">{articleHeader.title}</div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-[var(--muted)]">
              <span>分类 {articleHeader.category}</span>
              <span>时间 {articleHeader.published}</span>
              <span className="truncate">标签 {articleHeader.tags}</span>
            </div>
            {isAdmin && selectedArticlePath ? (
              <div className="mt-2 grid grid-cols-[180px_minmax(0,1fr)_auto] items-center gap-2">
                <select
                  className="h-8 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-slate-100"
                  value={metaCategory}
                  onChange={(event) => setMetaCategory(event.target.value)}
                >
                  {adminCategoryOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <input
                  className="h-8 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-slate-100"
                  placeholder="标签，逗号分隔"
                  value={metaTags}
                  onChange={(event) => setMetaTags(event.target.value)}
                />
                <button
                  className="h-8 rounded-md border border-[var(--border)] bg-[#0c0f14] px-3 text-xs text-slate-100 disabled:opacity-45"
                  disabled={updateMetaMutation.isPending}
                  onClick={() => updateMetaMutation.mutate()}
                >
                  {updateMetaMutation.isPending ? "保存中..." : "保存分类/标签"}
                </button>
              </div>
            ) : null}
          </div>
          <div className="terminal-scrollbar news-article-body min-h-0 flex-1 overflow-auto px-4 py-3 text-[15px] leading-7 text-slate-200">
            {articleQuery.isLoading ? <div className="text-[11px] text-[var(--muted)]">加载中...</div> : null}
            {!articleQuery.isLoading && !articleQuery.data ? (
              <div className="text-[11px] text-[var(--muted)]">请选择左侧资讯</div>
            ) : null}
            {articleQuery.data ? <div dangerouslySetInnerHTML={{ __html: articleQuery.data.content_html || "" }} /> : null}
          </div>
        </section>
      </div>
    </main>
  );
}
